import { ApiError } from "@carepass/api-client";
import { memberApiClient, partnerApiClient, publicApiClient } from "../../app/runtime";
import type { IdentityVerificationChallenge, IdentityVerificationInput } from "./identity-verification";
import type {
  BenefitClaim,
  BenefitType,
  BenefitEligibility,
  ContentPost,
  FileOperation,
  Inquiry,
  MemberProfile,
  MemberSession,
  MemberToken,
  MemberPasswordChangeProof,
  Notification,
  PlanChangeQuote,
  PartnerMobilePayment,
  PartnerSession,
  PartnerTerm,
  PublicTerm,
  Refund,
  SubscriptionDashboard,
  SubscriptionPayment,
  SubscriptionPlan,
  TermAgreement,
  TerminationPreview,
  TerminationResultSession,
  TerminationReceipt,
  Transaction,
  TransactionCategory,
  TransactionOverview,
} from "./types";

export const publicApi = {
  terms: async (termCode: string) => (
    await publicApiClient.get<PublicTerm[]>("/api/v1/public/terms", { query: { termCode }, cache: "no-store" })
  ).data,
  plans: async () => (await publicApiClient.get<SubscriptionPlan[]>("/api/v1/public/subscription-plans")).data,
  contents: async (type: string) => (
    await publicApiClient.get<ContentPost[]>("/api/v1/public/contents", { query: { type } })
  ).data,
  content: async (id: number) => (
    await publicApiClient.get<ContentPost>(`/api/v1/public/contents/${id}`)
  ).data,
  contentAttachment: async (contentId: number, fileAssetId: number) => (
    await publicApiClient.get<Blob>(
      `/api/v1/public/contents/${contentId}/attachments/${fileAssetId}`,
      { responseType: "blob", cache: "no-store" },
    )
  ).data,
  term: async (id: number) => (
    await publicApiClient.get<PublicTerm>(`/api/v1/public/terms/${id}`)
  ).data,
};

export const terminationResultApi = {
  issueSession: async (verificationToken: string) => (
    await publicApiClient.post<TerminationResultSession, { verificationToken: string }>(
      "/api/v1/public/termination-result-sessions",
      { verificationToken },
    )
  ).data,
  current: async (accessToken: string) => (
    await publicApiClient.get<Refund>("/api/v1/public/termination-results/current", {
      headers: { Authorization: `Bearer ${accessToken}` },
      cache: "no-store",
    })
  ).data,
};

export const authApi = {
  entryDecision: async (body: { phoneNumber: string }) => (
    await publicApiClient.post<{ nextAction: "PASSWORD" | "IDENTITY_VERIFICATION" | "SIGNUP"; flowToken: string; expiresAt: string }, typeof body>(
      "/api/v1/member/auth/entry-decisions",
      body,
    )
  ).data,
  login: async (body: { phoneNumber: string; password: string; deviceId: string }) => (
    await publicApiClient.post<MemberToken, typeof body>("/api/v1/member/auth/login", body)
  ).data,
  requestIdentity: async (body: IdentityVerificationInput & { purpose: string }) => (
    await publicApiClient.post<IdentityVerificationChallenge, typeof body>(
      "/api/v1/member/auth/identity-verifications",
      body,
    )
  ).data,
  confirmIdentity: async (token: string, verificationCode: string) => (
    await publicApiClient.post<IdentityVerificationChallenge, { verificationCode: string }>(
      `/api/v1/member/auth/identity-verifications/${token}/confirm`,
      { verificationCode },
    )
  ).data,
  identityStatus: async (token: string) => (
    await publicApiClient.get<IdentityVerificationChallenge>(`/api/v1/member/auth/identity-verifications/${token}`, { cache: "no-store" })
  ).data,
  abandonIdentity: async (token: string) => (
    await publicApiClient.post<void>(`/api/v1/member/auth/identity-verifications/${token}/abandon`, undefined, { keepalive: true, cache: "no-store" })
  ).data,
  resendIdentity: async (token: string) => (
    await publicApiClient.post<IdentityVerificationChallenge>(`/api/v1/member/auth/identity-verifications/${token}/resend`)
  ).data,
  completeIdentity: async (token: string) => (
    await publicApiClient.post<IdentityVerificationChallenge>(`/api/v1/member/auth/identity-verifications/${token}/complete`)
  ).data,
  activate: async (token: string, body: { password: string; passwordConfirmation: string; deviceId: string }) => (
    await publicApiClient.post<MemberToken, typeof body>(`/api/v1/member/auth/activations/${token}/complete`, body)
  ).data,
  resetPassword: async (token: string, body: { password: string; passwordConfirmation: string }) => (
    await publicApiClient.post<void, typeof body>(`/api/v1/member/auth/password-resets/${token}/complete`, body)
  ).data,
  verifyPasswordChange: async (body: { currentPassword: string }) => (
    await memberApiClient.post<MemberPasswordChangeProof, typeof body>("/api/v1/member/auth/password-changes/verify", body)
  ).data,
  completePasswordChange: async (body: { changeToken: string; password: string; passwordConfirmation: string }) => (
    await memberApiClient.post<void, typeof body>("/api/v1/member/auth/password-changes/complete", body)
  ).data,
  abandonPasswordChange: async (changeToken: string) => (
    await memberApiClient.post<void, { changeToken: string }>("/api/v1/member/auth/password-changes/abandon", { changeToken }, { keepalive: true, cache: "no-store" })
  ).data,
};

export const partnerApi = {
  duplicateReturn: async (token: string, phoneNumber: string) => (
    await partnerApiClient.post<{ status: string; result: string; resultCode: string; resultMessage: string; returnUrl: string; fallbackToHome: boolean }, { phoneNumber: string }>(`/api/v1/partner/join/${token}/duplicate-return`, { phoneNumber })
  ).data,
  prepareReturn: async (token: string) => (
    await partnerApiClient.get<{ returnUrl: string; callbackDelivered: boolean; callbackRequired: boolean }>(`/api/v1/partner/join/${token}/return-ready`, { cache: "no-store" })
  ).data,
  open: async (query: { channel: string; banner_cd: string; return_url: string; trid: string; callback_url: string; custom_parameter?: string }) => (
    await partnerApiClient.get<PartnerSession>("/api/v1/partner/join", { query })
  ).data,
  status: async (token: string) => (
    await partnerApiClient.get<PartnerSession & { result?: string; resultCode?: string; resultMessage?: string }>(
      `/api/v1/partner/join/${token}`,
    )
  ).data,
  terms: async (token: string) => (
    await partnerApiClient.get<PartnerTerm[]>(`/api/v1/partner/join/${token}/terms`)
  ).data,
  agree: async (token: string, agreements: readonly { termVersionId: number; agreed: boolean }[]) => (
    await partnerApiClient.post<PartnerSession, { agreements: typeof agreements }>(
      `/api/v1/partner/join/${token}/terms/agreements`,
      { agreements },
    )
  ).data,
  registerContact: async (token: string, body: { phoneNumber: string; telecomCode: string; birthDate: string; genderCode: string }) => (
    await partnerApiClient.post<{ status: string }, typeof body>(`/api/v1/partner/join/${token}/contact`, body)
  ).data,
  requestMobilePayment: async (token: string, body: { requestKey: string; socialNumber: string; phoneNumber: string; telecomCode: string; mvnoNetwork?: string; agreements: readonly { termVersionId: number; agreed: boolean }[] }) => (
    await partnerApiClient.post<PartnerMobilePayment, typeof body>(`/api/v1/partner/join/${token}/mobile-payments`, body)
  ).data,
  mobilePayment: async (token: string, attemptToken: string) => (
    await partnerApiClient.get<PartnerMobilePayment>(`/api/v1/partner/join/${token}/mobile-payments/${attemptToken}`)
  ).data,
  confirmMobilePayment: async (token: string, attemptToken: string, body: { verificationCode: string; socialNumber: string }) => (
    await partnerApiClient.post<PartnerMobilePayment, typeof body>(`/api/v1/partner/join/${token}/mobile-payments/${attemptToken}/confirm`, body)
  ).data,
  resendMobilePayment: async (token: string, attemptToken: string, body: { socialNumber: string }) => (
    await partnerApiClient.post<PartnerMobilePayment, typeof body>(`/api/v1/partner/join/${token}/mobile-payments/${attemptToken}/resend`, body)
  ).data,
  invalidateMobilePayment: async (token: string, attemptToken: string) => (
    await partnerApiClient.post<PartnerMobilePayment>(`/api/v1/partner/join/${token}/mobile-payments/${attemptToken}/invalidate`)
  ).data,
  completeMobilePayment: async (token: string, attemptToken: string) => (
    await partnerApiClient.post<PartnerMobilePayment>(`/api/v1/partner/join/${token}/mobile-payments/${attemptToken}/complete`)
  ).data,
  complete: async (token: string) => (
    await partnerApiClient.post<{ status: string; result: string; resultCode: string; resultMessage: string; checkoutUrl?: string; mobileCheckoutUrl?: string; onlineCheckoutUrl?: string }>(
      `/api/v1/partner/join/${token}/complete`,
    )
  ).data,
  cancel: async (token: string) => (
    await partnerApiClient.post<PartnerSession>(`/api/v1/partner/join/${token}/cancel`)
  ).data,
};

export const directSignupApi = {
  retryPayment: async (token: string) => (
    await publicApiClient.post<{ sessionToken: string; expiresAt: string }>(
      `/api/v1/public/signups/${token}/payments/retry`,
    )
  ).data,
  setPassword: async (token: string, body: { password: string; passwordConfirmation: string }) => (
    await publicApiClient.post<{ signupCompleted: boolean; memberStatus: string; passwordSet: boolean }, typeof body>(
      `/api/v1/public/signups/${token}/password`, body,
    )
  ).data,
  payment: async (token: string) => (
    await publicApiClient.post<{
      status: string; result: string; resultCode: string; resultMessage: string;
      planCode: string; planName: string; paymentAmount: number;
      billingKeyRegistered: boolean; paymentCompletedAt?: string | null;
      signupCompleted: boolean; memberStatus?: string | null; activationRequired: boolean;
      checkoutUrl?: string;
      mobileCheckoutUrl?: string;
      onlineCheckoutUrl?: string;
    }>(`/api/v1/public/signups/${token}/payments`)
  ).data,
  open: async (planCode: string) => (
    await publicApiClient.post<PartnerSession, { planCode: string }>("/api/v1/public/signups", { planCode })
  ).data,
  status: async (token: string) => (
    await publicApiClient.get<import("./types").DirectSignupSession & { result?: string; resultCode?: string; resultMessage?: string }>(
      `/api/v1/public/signups/${token}`,
    )
  ).data,
  terms: async (token: string) => (
    await publicApiClient.get<PartnerTerm[]>(`/api/v1/public/signups/${token}/terms`)
  ).data,
  agree: async (token: string, agreements: readonly { termVersionId: number; agreed: boolean }[]) => (
    await publicApiClient.post<{ status: string }, { agreements: typeof agreements }>(
      `/api/v1/public/signups/${token}/terms/agreements`,
      { agreements },
    )
  ).data,
  requestIdentity: async (token: string, body: IdentityVerificationInput) => (
    await publicApiClient.post<IdentityVerificationChallenge, typeof body>(
      `/api/v1/public/signups/${token}/identity-verifications`,
      body,
    )
  ).data,
  confirmIdentity: async (token: string, verificationToken: string, verificationCode: string) => (
    await publicApiClient.post<IdentityVerificationChallenge, { verificationCode: string }>(
      `/api/v1/public/signups/${token}/identity-verifications/${verificationToken}/confirm`,
      { verificationCode },
    )
  ).data,
  identityStatus: async (token: string, verificationToken: string) => (
    await publicApiClient.get<IdentityVerificationChallenge>(`/api/v1/public/signups/${token}/identity-verifications/${verificationToken}`, { cache: "no-store" })
  ).data,
  resendIdentity: async (token: string, verificationToken: string) => (
    await publicApiClient.post<IdentityVerificationChallenge>(`/api/v1/public/signups/${token}/identity-verifications/${verificationToken}/resend`)
  ).data,
  completeIdentity: async (token: string, verificationToken: string) => (
    await publicApiClient.post<IdentityVerificationChallenge>(`/api/v1/public/signups/${token}/identity-verifications/${verificationToken}/complete`)
  ).data,
  cancel: async (token: string) => (
    await publicApiClient.post<{ status: string; result: string; cancellationRequested?: boolean; paymentResolution?: string | null; resultMessage?: string | null }>(`/api/v1/public/signups/${token}/cancel`)
  ).data,
  reconcilePayment: async (token: string) => (
    await publicApiClient.post<{ status: string; result: string | null; cancellationRequested: boolean; paymentResolution?: string | null; resultMessage?: string | null }>(`/api/v1/public/signups/${token}/payments/reconcile`)
  ).data,
};

export const memberApi = {
  me: async () => (await memberApiClient.get<{ memberId: number; memberStatus: string }>("/api/v1/member/me")).data,
  profile: async () => (await memberApiClient.get<MemberProfile>("/api/v1/member/profile")).data,
  subscription: async () => (
    await memberApiClient.get<SubscriptionDashboard>("/api/v1/member/subscription")
  ).data,
  banks: async () => (await memberApiClient.get<import("./types").Bank[]>("/api/v1/member/banks")).data,
  subscriptionPlans: async () => (
    await memberApiClient.get<SubscriptionPlan[]>("/api/v1/member/subscription/plans")
  ).data,
  subscriptionPayments: async () => (
    await memberApiClient.get<SubscriptionPayment[]>("/api/v1/member/subscription/payments")
  ).data,
  planChangeQuote: async (planCode: string) => (
    await memberApiClient.get<PlanChangeQuote>("/api/v1/member/subscription/plan-changes/quote", {
      query: { planCode },
    })
  ).data,
  transactions: async () => (
    await memberApiClient.get<Transaction[]>("/api/v1/member/transactions")
  ).data,
  transaction: async (id: number) => (
    await memberApiClient.get<Transaction>(`/api/v1/member/transactions/${id}`)
  ).data,
  transactionOverview: async () => (
    await memberApiClient.get<TransactionOverview>("/api/v1/member/transactions/overview")
  ).data,
  categories: async () => (
    await memberApiClient.get<TransactionCategory[]>("/api/v1/member/transactions/categories")
  ).data,
  benefits: async () => (await memberApiClient.get<BenefitClaim[]>("/api/v1/member/benefits")).data,
  benefit: async (id: number) => (
    await memberApiClient.get<BenefitClaim>(`/api/v1/member/benefits/${id}`)
  ).data,
  benefitEligibility: async () => (
    await memberApiClient.get<BenefitEligibility>("/api/v1/member/benefits/eligibility")
  ).data,
  benefitTypes: async () => (
    await memberApiClient.get<BenefitType[]>("/api/v1/member/benefits/types")
  ).data,
  inquiries: async () => (await memberApiClient.get<Inquiry[]>("/api/v1/member/inquiries")).data,
  inquiry: async (id: number) => (
    await memberApiClient.get<Inquiry>(`/api/v1/member/inquiries/${id}`)
  ).data,
  notifications: async () => (
    await memberApiClient.get<Notification[]>("/api/v1/member/notifications")
  ).data,
  sessions: async () => (await memberApiClient.get<MemberSession[]>("/api/v1/member/sessions")).data,
  terms: async () => (
    await memberApiClient.get<TermAgreement[]>("/api/v1/member/profile/term-agreements")
  ).data,
  termination: async () => (await memberApiClient.get<Refund>("/api/v1/member/termination")).data,
  terminationPreview: async () => (
    await memberApiClient.get<TerminationPreview>("/api/v1/member/termination/preview")
  ).data,
  updateEmail: async (email: string) => (
    await memberApiClient.put<MemberProfile, { email: string }>("/api/v1/member/profile/email", { email })
  ).data,
  payTransaction: async (id: number) => (
    await memberApiClient.post<Transaction>(`/api/v1/member/transactions/${id}/payments`)
  ).data,
  reconcileTransactionPayment: async (id: number) => (
    await memberApiClient.post<Transaction>(`/api/v1/member/transactions/${id}/payments/reconcile`)
  ).data,
  resetTransactionPayment: async (id: number) => (
    await memberApiClient.post<Transaction>(`/api/v1/member/transactions/${id}/payments/reset`)
  ).data,
  selectTransfer: async (id: number, body: { scheduleType: "IMMEDIATE" | "SCHEDULED"; requestedTransferDate?: string }, requestKey: string) => (
    await memberApiClient.post<Transaction, typeof body>(`/api/v1/member/transactions/${id}/transfers`, body, {
      headers: { "Idempotency-Key": requestKey },
    })
  ).data,
  cancelCardPayment: async (id: number, requestKey: string) => (
    await memberApiClient.post<Transaction>(`/api/v1/member/transactions/${id}/card-payment/cancellations`, undefined, {
      headers: { "Idempotency-Key": requestKey },
    })
  ).data,
  cancelTransaction: async (id: number, requestKey: string) => (
    await memberApiClient.post<Transaction>(`/api/v1/member/transactions/${id}/cancellations`, undefined, {
      headers: { "Idempotency-Key": requestKey },
    })
  ).data,
  verifyAccount: async (body: { bankCode: string; accountNumber: string }) => (
    await memberApiClient.post<{
      id: number;
      resultCode: string;
      resultMessage: string;
      accountNumberMasked: string;
      accountHolderMasked: string;
    }, typeof body>("/api/v1/member/transactions/account-verifications", body)
  ).data,
  createTransaction: async (body: {
    categoryCode: string;
    title: string;
    requestedAmount: number;
    accountVerificationId: number;
    remittanceMemo?: string;
    documents: readonly { fileAssetId: number; documentTypeCode: string }[];
  }, requestKey: string) => (
    await memberApiClient.post<Transaction, typeof body>("/api/v1/member/transactions", body, {
      headers: { "Idempotency-Key": requestKey },
    })
  ).data,
  submitSupplement: async (id: number, documents: readonly { fileAssetId: number; documentTypeCode: string }[]) => (
    await memberApiClient.post<Transaction, { documents: typeof documents }>(
      `/api/v1/member/transactions/${id}/supplement-submissions`,
      { documents },
    )
  ).data,
  createBenefit: async (body: {
    claimType: string;
    incidentDate: string;
    description: string;
    requestedAmount: number;
    documentFileIds: readonly number[];
  }) => (await memberApiClient.post<BenefitClaim, typeof body>("/api/v1/member/benefits", body)).data,
  createInquiry: async (body: {
    categoryCode: string;
    title: string;
    content: string;
    fileAssetIds: readonly number[];
  }) => (await memberApiClient.post<Inquiry, typeof body>("/api/v1/member/inquiries", body)).data,
  changePlan: async (body: { planCode: string; password: string; quoteToken: string; refundAccount?: { bankCode: string; accountNumber: string; accountHolder: string } }) => (
    await memberApiClient.post<{
      subscriptionId: number;
      fromPlanCode: string;
      toPlanCode: string;
      deferred?: boolean;
      proratedChargeAmount: number;
      proratedRefundAmount: number;
      effectiveAt: string;
      refundId?: number | null;
    }, typeof body>("/api/v1/member/subscription/plan-changes", body)
  ).data,
  saveTermAgreement: async (body: { termVersionId: number; agreed: boolean }) => (
    await memberApiClient.post<TermAgreement, typeof body>("/api/v1/member/profile/term-agreements", body)
  ).data,
  requestTermination: async (body: {
    quoteToken: string;
    reason: string;
    agreedToRefundPolicy: boolean;
    confirmed: boolean;
    bankCode?: string;
    accountNumber?: string;
    accountHolder?: string;
  }) => (await memberApiClient.post<TerminationReceipt, typeof body>("/api/v1/member/termination", body)).data,
  readNotification: async (id: number) => (
    await memberApiClient.post<Notification>(`/api/v1/member/notifications/${id}/read`)
  ).data,
  readAllNotifications: async () => (
    await memberApiClient.post<Notification[]>("/api/v1/member/notifications/read-all")
  ).data,
  deleteSession: async (id: number) => memberApiClient.delete<void>(`/api/v1/member/sessions/${id}`),
  hideInquiry: async (id: number) => memberApiClient.delete<void>(`/api/v1/member/inquiries/${id}`),
  logout: async () => memberApiClient.post<void>("/api/v1/member/logout"),
};

export async function uploadMemberFile(file: File, purpose: string): Promise<number> {
  const requested = (
    await memberApiClient.post<FileOperation, {
      purpose: string;
      originalFilename: string;
      contentType: string;
      fileSize: number;
    }>("/api/v1/member/files/upload-requests", {
      purpose,
      originalFilename: file.name,
      contentType: file.type || "application/octet-stream",
      fileSize: file.size,
    })
  ).data;
  if (!requested.uploadToken) throw new Error("업로드 토큰을 받지 못했습니다.");
  await memberApiClient.put<FileOperation, ArrayBuffer>(
    `/api/v1/member/files/upload-requests/${requested.operationId}/content`,
    await file.arrayBuffer(),
    {
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "X-Carepass-Upload-Token": requested.uploadToken,
      },
    },
  );
  const deadline = Date.now() + 30_000;
  while (Date.now() < deadline) {
    const operation = (
      await memberApiClient.get<FileOperation>(`/api/v1/member/files/operations/${requested.operationId}`)
    ).data;
    if (operation.status === "READY" && operation.fileAssetId) return operation.fileAssetId;
    if (operation.status === "FAILED" || operation.status === "EXPIRED") {
      throw new Error(operation.failureMessage ?? "파일을 사용할 수 없습니다.");
    }
    await new Promise((resolve) => window.setTimeout(resolve, 750));
  }
  throw new ApiError({
    kind: "network",
    code: "FILE_PROCESSING_TIMEOUT",
    message: "파일 검사 결과를 확인하지 못했습니다.",
  });
}

export async function downloadMemberFile(fileAssetId: number): Promise<Blob> {
  const requested = (
    await memberApiClient.post<FileOperation>(`/api/v1/member/files/${fileAssetId}/download-requests`)
  ).data;
  const deadline = Date.now() + 30_000;
  let operation = requested;
  while (operation.status !== "READY") {
    if (operation.status === "FAILED" || operation.status === "EXPIRED") {
      throw new Error(operation.failureMessage ?? "파일을 내려받을 수 없습니다.");
    }
    if (Date.now() >= deadline) {
      throw new ApiError({ kind: "network", code: "FILE_PROCESSING_TIMEOUT", message: "파일 준비 결과를 확인하지 못했습니다." });
    }
    await new Promise((resolve) => window.setTimeout(resolve, 750));
    operation = (
      await memberApiClient.get<FileOperation>(`/api/v1/member/files/operations/${requested.operationId}`)
    ).data;
  }
  return (
    await memberApiClient.get<Blob>(`/api/v1/member/files/downloads/${operation.operationId}`, {
      responseType: "blob",
      cache: "no-store",
    })
  ).data;
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
